import React, { useEffect, useRef, useState } from "react";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { Skeleton } from "@mui/material";
import ReactPaginate from "react-paginate";
import DateConverter from "@heartinz/date_and_time_processor";
import { useAuth } from "../../../context/AuthProvider";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useShowAlert } from "../../../context/AlertContext";
import { useNetwork } from "../../../context/NetworkContext";
import handleError from "../../../constants/handleError";
import EditJobcard from "../jobcard/Editjobcard";
import Table from "../../../constants/Table";

const JobCardList = ({ customerId }) => {
  const { isUser } = useAuth();
  const hasFetched = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentEstimatePage, setCurrentEstimatePage] = useState(0);
  const [totalEstimatePages, setTotalEstimatePages] = useState(0);
  const [totalEstimateItems, setTotalEstimateItems] = useState(0);

  const perPage = 10;
  const { checkNetwork } = useNetwork();
  const showAlert = useShowAlert();

  const [isEditData, setEditData] = useState(null);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [taskTitleSuggest, setTaskTitleSuggest] = useState([]);
  const [attributesTitleSuggest, setAttributesTitleSuggest] = useState([]);
  const [isjobCardData, setJobCardData] = useState([]);

  const handleListJobCard = async (customerId, page = 0) => {
    try {
      if (!checkNetwork()) return;
      setIsLoading(true);
      const response = await AxiosInstance.get(
        `module/jobcard/customer/${customerId}?pageno=${
          page + 1
        }&limit=${perPage}`
      );
      const results = response.data.results || [];
      // console.log(results);
      setJobCardData(results);
      if (results.length === 0) {
        setTotalEstimatePages(0);
        setTotalEstimateItems(0);
        setCurrentEstimatePage(0);
      } else {
        setTotalEstimatePages(response.data.totalpages || 0);
        setTotalEstimateItems(response.data.totalitems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobcardUpdate = (updatedJobcard) => {
    setJobCardData((prev) =>
      prev.map((item) =>
        item._id === updatedJobcard._id ? updatedJobcard : item
      )
    );
  };

  const handleJobcardFieldUpdate = (jobcardId, updatedFields) => {
    setJobCardData((prev) =>
      prev.map((item) =>
        item._id === jobcardId
          ? {
              ...item,
              ...updatedFields,
              customer: updatedFields.customer || item.customer,
              customerName: updatedFields.customerName || item.customerName,
              customerPhone: updatedFields.customerPhone || item.customerPhone,
              customerid: updatedFields.customerid || item.customerid,
            }
          : item
      )
    );
  };

  const handleFieldUpdate = async (jobcardId, field, value) => {
    try {
      if (!checkNetwork()) return;
      const payload = new FormData();

      if (field === "mediaFile" && value instanceof File) {
        payload.append("media", value);
      } else if (field === "attributes" && Array.isArray(value)) {
        payload.append(field, JSON.stringify(value));
      } else if (field === "customerid" && value?._id) {
        payload.append("customerid", value._id);
      } else {
        payload.append(field, value ?? "");
      }

      const res = await AxiosInstance.patch(
        `/module/jobcard/${jobcardId}`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const updatedData = res.data.results;

      // ✅ Update only that jobcard in the flat list
      setJobCardData((prev) =>
        prev.map((item) =>
          item._id === jobcardId ? { ...item, ...updatedData } : item
        )
      );

      showAlert("success", `${field} updated successfully`);
      return updatedData;
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const openEditModal = async (cItem) => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(`/module/jobcard/${cItem._id}`);
      const data = response.data.results;
      setEditData({
        ...data,
        mediaFile: data.media?.length ? data.media[0] : null,
        tasks: data.task || [],
      });
      setEditModalOpen(true);
    } catch (error) {
      handleError(error, showAlert);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
  };

  const handleTaskSuggestion = async () => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/jobcard/suggestion/task/title`
      );

      setTaskTitleSuggest(response.data.results || []);
      // setShowtitlePopup(true);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  const handleAttributesSuggestion = async () => {
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/jobcard/suggestion/attributes`
      );
      setAttributesTitleSuggest(response.data.results || []);
      // setShowtitlePopup(true);
    } catch (error) {
      handleError(error, showAlert);
      return false;
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    handleTaskSuggestion();
    handleAttributesSuggestion();
    if (customerId) {
      handleListJobCard(customerId, currentEstimatePage);
    }
  }, [customerId]);

  return (
    <div>
      
      <Table
        loading={isLoading}
        data={isjobCardData}
        onRowClick={(item) => openEditModal(item)}
        columns={[
          {
            label: "S.No",
            render: (_, index) => currentEstimatePage * perPage + index + 1,
          },
          {
            label: "Job No",
            render: (item) => (
              <span
                className="badge rounded-pill"
                style={{
                  background: "#ffab1d",
                  color: "#000",
                  border: "none",
                }}
              >
                {item.jobNo}
              </span>
            ),
          },
          {
            label: "Title",
            render: (item) => (
              <div className="d-flex flex-column">
                <span className="fw-medium">{item.title}</span>
              </div>
            ),
          },
          {
            label: "Status",
            render: (item) => (
              <span
                className={` text-capitalize badge rounded-pill ${
                  item.status?.toLowerCase() === "pending"
                    ? "bg-label-warning"
                    : item.status?.toLowerCase() === "inprogress"
                    ? "bg-label-primary"
                    : item.status?.toLowerCase() === "ready"
                    ? "bg-label-blue"
                    : item.status?.toLowerCase() === "delivered"
                    ? "bg-label-green"
                    : item.status?.toLowerCase() === "cancelled"
                    ? "bg-label-danger"
                    : ""
                }`}
              >
                {item.status ? item.status.toLowerCase() : "-"}
              </span>
            ),
          },
          {
            label: "Due Date",
            render: (item) => (
              <div className="d-flex flex-column">
                <span className="text-nowrap text-heading fw-medium">
                  {item?.dueDate
                    ? DateConverter.ISOToDisplayFormat(
                        DateConverter.convertToTimeZone(
                          item?.dueDate,
                          isUser?.timezone
                        )
                      ).date
                    : "-"}
                </span>
              </div>
            ),
          },
          {
            label: "Task",
            render: (item) => (
              <>
                <span className="badge bg-label-spring">
                  {item?.task
                    ? item.task.filter(
                        (t) => t.status?.toLowerCase() === "completed"
                      ).length
                    : 0}
                </span>{" "}
                /
                <span className="badge bg-label-space mx-1">
                  {item?.task
                    ? item.task.filter(
                        (t) => t.status?.toLowerCase() !== "cancelled"
                      ).length
                    : 0}
                </span>
              </>
            ),
          },
          {
            label: "Priority",
            render: (item) => (
              <>
                {item.priority ? (
                  <span
                    className={`badge rounded-pill  ${
                      item.priority.toLowerCase() === "high"
                        ? "bg-label-danger"
                        : item.priority.toLowerCase() === "medium"
                        ? "bg-label-warning"
                        : "bg-label-success"
                    } text-capitalize`}
                  >
                    {item.priority.toLowerCase()}
                  </span>
                ) : (
                  " - "
                )}
              </>
            ),
          },
          {
            label: "Advance Paid",
            render: (item) =>
              item?.totalAdvancePaid && item.totalAdvancePaid > 0 ? (
                <span className="badge rounded-pill bg-label-fall">
                  ₹ {CurrencyConverter.toCurrency(item.totalAdvancePaid, "INR")}
                </span>
              ) : (
                " - "
              ),
          },
        ].filter(Boolean)}
      />
      {totalEstimatePages > 0 && (
        <div className="d-flex justify-content-center mt-3">
          <ReactPaginate
            previousLabel={"← Previous"}
            nextLabel={"Next →"}
            breakLabel={"..."}
            onPageChange={({ selected }) => {
              setIsLoading(true);
              setCurrentEstimatePage(selected);
            }}
            pageRangeDisplayed={5}
            pageCount={totalEstimatePages}
            containerClassName="pagination"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            previousClassName="page-item"
            previousLinkClassName="page-link"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakClassName="page-item"
            breakLinkClassName="page-link"
            activeClassName="active"
            forcePage={currentEstimatePage}
          />
        </div>
      )}

      {isEditModalOpen && (
        <EditJobcard
          onClose={closeEditModal}
          editData={isEditData}
          onJobcardUpdate={handleJobcardUpdate}
          onFieldUpdate={handleFieldUpdate}
          titleSuggest={taskTitleSuggest}
          attributeSuggest={attributesTitleSuggest}
          handleJobcardFieldUpdate={handleJobcardFieldUpdate}
        />
      )}
    </div>
  );
};

export default JobCardList;
